/**
 * LeerpuntenPanel — de Leerpunten-tab op /telefoon.
 *
 * Review-werkvoorraad voor de voice-agent (agent_leerpunten): elk voice-agent-
 * gesprek wordt door de coach beoordeeld (fixtype kb/prompt/verb/bug), en
 * kantoor kan er zelf punten bij schrijven vanuit de gespreksdrawer.
 *
 * De mens is hier auteur, niet alleen jurylid: je kunt het voorstel herschrijven
 * vóór je akkoord geeft. Het coach-origineel blijft staan (`voorstel`), jouw
 * versie landt in `voorstel_definitief`, en het verschil is de kalibratiemeting
 * voor de coach. Dat dekt "half eens" en "probleem klopt, oplossing niet"
 * zonder een vijfde status.
 *
 * Goedkeuren van een kb-leerpunt maakt het VASTGESTELDE voorstel direct een
 * actief kennisbank-artikel (geen deploy nodig; per gesprek vers ingeladen).
 * Goedkeuren van prompt/verb/bug zet 'akkoord': doorvoeren gebeurt in een
 * werksessie (verb = PR, prompt = git + droplet-deploy).
 *
 * Endpoint is admin-only; niet-admins zien een nette melding.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useCallDrawerStore } from '../../stores/call-drawer-store';

type Status = 'voorgesteld' | 'akkoord' | 'toegepast' | 'afgewezen';

interface Beoordeling {
  call_id: string | null;
  brand: string | null;
  oordeel: string | null;
  model: string | null;
  created_at: string;
}

interface Leerpunt {
  id: string;
  klasse_id?: string | null;
  call_id: string | null;
  fixtype: 'kb' | 'prompt' | 'verb' | 'bug' | string;
  leerpunt: string;
  bewijs: string | null;
  voorstel: Record<string, unknown> | null;
  voorstel_definitief: Record<string, unknown> | null;
  bron: string | null;
  reviewer_notitie: string | null;
  reviewed_by: string | null;
  zekerheid: string | null;
  status: Status;
  toegepast_ref: string | null;
  created_at: string;
  reviewed_at: string | null;
  beoordeling: Beoordeling | null;
}

interface KlasseLid {
  id: string;
  call_id: string | null;
  leerpunt: string;
  bewijs: string | null;
  zekerheid: string | null;
  created_at: string;
}

interface Klasse {
  id: string;
  titel: string;
  beschrijving: string;
  fixtype: string;
  voorstel: Record<string, unknown> | null;
  voorstel_definitief: Record<string, unknown> | null;
  huisregel_kandidaat: string | null;
  aantal: number;
  voorbeeld_bewijs: string[] | null;
  status: string;
  leden: KlasseLid[];
}

const STATUSSEN: Array<[Status, string]> = [
  ['voorgesteld', 'Voorgesteld'],
  ['akkoord', 'Akkoord'],
  ['toegepast', 'Toegepast'],
  ['afgewezen', 'Afgewezen'],
];

const FIXTYPE_STIJL: Record<string, string> = {
  kb: 'bg-emerald-100 text-emerald-800',
  prompt: 'bg-indigo-100 text-indigo-800',
  verb: 'bg-amber-100 text-amber-800',
  bug: 'bg-rose-100 text-rose-800',
};

const FIXTYPE_UITLEG: Record<string, string> = {
  kb: 'kennisbank — goedkeuren maakt direct een actief artikel',
  prompt: 'promptregel — goedkeuren zet akkoord, doorvoeren in een werksessie',
  verb: 'tool/verb-wijziging — goedkeuren zet akkoord, doorvoeren als PR',
  bug: 'technisch defect — goedkeuren zet akkoord, onderzoeken in een werksessie',
};

/** Bekende voorstel-velden in leesvolgorde; de rest valt terug op JSON. */
const VOORSTEL_VELDEN: Array<[string, string]> = [
  ['titel', 'Titel'],
  ['antwoord', 'Antwoord'],
  ['vraag_patronen', 'Vraagpatronen'],
  ['beschrijving', 'Beschrijving'],
  ['voorstel_tekst', 'Voorgestelde tekst'],
  ['verb', 'Verb'],
  ['observatie', 'Observatie'],
];

/** Wat je per fixtype kunt bewerken, in invulvolgorde. `lang` = textarea. */
const BEWERKBAAR: Record<string, Array<{ key: string; label: string; lang?: boolean }>> = {
  kb: [
    { key: 'brand', label: 'Merk (KLEDA/CINCO/SOFASAVER/SCHOONHUISSHOP/ALL)' },
    { key: 'titel', label: 'Titel' },
    { key: 'vraag_patronen', label: 'Vraagpatronen (één per regel)', lang: true },
    { key: 'antwoord', label: 'Antwoord', lang: true },
  ],
  prompt: [
    { key: 'beschrijving', label: 'Beschrijving', lang: true },
    { key: 'voorstel_tekst', label: 'Voorgestelde tekst (komt zo in de prompt)', lang: true },
  ],
  verb: [
    { key: 'verb', label: 'Verb' },
    { key: 'observatie', label: 'Observatie', lang: true },
  ],
  bug: [{ key: 'beschrijving', label: 'Beschrijving', lang: true }],
};

/** Wat de mens vaststelde wint van wat de coach schreef (zelfde regel als de API). */
function definitief(lp: Leerpunt): Record<string, unknown> | null {
  return lp.voorstel_definitief ?? lp.voorstel;
}

function VoorstelBlok({ voorstel }: { voorstel: Record<string, unknown> | null }) {
  if (!voorstel || Object.keys(voorstel).length === 0) return null;
  const bekend = VOORSTEL_VELDEN.filter(([k]) => typeof voorstel[k] === 'string' && voorstel[k]);
  const bekendeKeys = new Set(bekend.map(([k]) => k));
  const rest = Object.entries(voorstel).filter(
    ([k, v]) => !bekendeKeys.has(k) && v !== null && v !== '' && k !== 'brand' && k !== 'geldig_tot',
  );
  return (
    <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
      {bekend.map(([k, label]) => (
        <div key={k}>
          <span className="text-xs font-medium text-slate-500">{label}</span>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{String(voorstel[k])}</p>
        </div>
      ))}
      {rest.length > 0 && (
        <pre className="text-xs text-slate-500 overflow-x-auto">
          {JSON.stringify(Object.fromEntries(rest), null, 1)}
        </pre>
      )}
    </div>
  );
}

export function LeerpuntenPanel({ onCountChange }: { onCountChange?: (n: number) => void }) {
  const [status, setStatus] = useState<Status>('voorgesteld');
  const [rows, setRows] = useState<Leerpunt[]>([]);
  const [totaal, setTotaal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [bewerkId, setBewerkId] = useState<string | null>(null);
  const [klassen, setKlassen] = useState<Klasse[]>([]);
  const [bewerkKlasseId, setBewerkKlasseId] = useState<string | null>(null);
  const [concept, setConcept] = useState<Record<string, string>>({});
  const [notitie, setNotitie] = useState('');
  const openCall = useCallDrawerStore((s) => s.open);

  const authHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const { data: { session } } = await supabase.auth.getSession();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
    return headers;
  }, []);

  const fetchRows = useCallback(async (s: Status) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const [r, rk] = await Promise.all([
        fetch(`/api/telephony/leerpunten?status=${s}`, { headers }),
        // Klassen horen alleen bij de open wachtrij; andere statussen tonen
        // gewoon de losse rijen (waar de cascade-notitie al op staat).
        s === 'voorgesteld'
          ? fetch('/api/telephony/leerpunten?view=klassen', { headers })
          : Promise.resolve(null),
      ]);
      if (r.status === 401 || r.status === 403) {
        throw new Error('Alleen admins kunnen leerpunten reviewen.');
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      const data: Leerpunt[] = json.leerpunten || [];
      const n: number = typeof json.totaal === 'number' ? json.totaal : data.length;
      setRows(data);
      setTotaal(n);
      if (rk && rk.ok) {
        const jk = await rk.json();
        setKlassen(jk.klassen || []);
      } else {
        setKlassen([]);
      }
      if (s === 'voorgesteld') onCountChange?.(n);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, onCountChange]);

  useEffect(() => {
    void fetchRows(status);
  }, [fetchRows, status]);

  const startBewerken = useCallback((lp: Leerpunt) => {
    const bron = definitief(lp) || {};
    const velden = BEWERKBAAR[lp.fixtype] || [];
    const start: Record<string, string> = {};
    for (const { key } of velden) start[key] = typeof bron[key] === 'string' ? String(bron[key]) : '';
    setConcept(start);
    setNotitie(lp.reviewer_notitie || '');
    setBewerkId(lp.id);
  }, []);

  const stopBewerken = useCallback(() => {
    setBewerkId(null);
    setConcept({});
    setNotitie('');
  }, []);

  const review = useCallback(async (lp: Leerpunt, action: 'approve' | 'reject') => {
    setBusyId(lp.id);
    setError(null);
    try {
      const bewerkt = bewerkId === lp.id;
      const headers = await authHeaders();
      const r = await fetch(`/api/telephony/leerpunten?id=${encodeURIComponent(lp.id)}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          action,
          ...(bewerkt ? { voorstel: concept, notitie } : {}),
        }),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
      stopBewerken();
      // Rij verdwijnt uit deze wachtrij; verse stand ophalen.
      await fetchRows(status);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Review mislukt');
    } finally {
      setBusyId(null);
    }
  }, [authHeaders, bewerkId, concept, fetchRows, notitie, status, stopBewerken]);

  const startKlasseBewerken = useCallback((k: Klasse) => {
    const bron = (k.voorstel_definitief ?? k.voorstel) || {};
    const velden = BEWERKBAAR[k.fixtype] || [];
    const start: Record<string, string> = {};
    for (const { key } of velden) start[key] = typeof bron[key] === 'string' ? String(bron[key]) : '';
    setConcept(start);
    setNotitie('');
    setBewerkKlasseId(k.id);
    setBewerkId(null);
  }, []);

  const reviewKlasse = useCallback(async (k: Klasse, action: 'approve' | 'reject') => {
    setBusyId(k.id);
    setError(null);
    try {
      const bewerkt = bewerkKlasseId === k.id;
      const headers = await authHeaders();
      const r = await fetch(`/api/telephony/leerpunten?klasse=${encodeURIComponent(k.id)}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          action,
          ...(bewerkt ? { voorstel: concept, notitie } : {}),
        }),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
      setBewerkKlasseId(null);
      setConcept({});
      setNotitie('');
      await fetchRows(status);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Klasse-review mislukt');
    } finally {
      setBusyId(null);
    }
  }, [authHeaders, bewerkKlasseId, concept, fetchRows, notitie, status]);

  // In de open wachtrij vertegenwoordigt de klasse-kaart zijn leden; los
  // tonen we alleen wat (nog) nergens bij hoort.
  const losseRows = status === 'voorgesteld' ? rows.filter((lp) => !lp.klasse_id) : rows;
  const inKlassen = status === 'voorgesteld' ? rows.length - losseRows.length : 0;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1">
          {STATUSSEN.map(([s, label]) => (
            <button
              key={s}
              type="button"
              onClick={() => { stopBewerken(); setStatus(s); }}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                status === s
                  ? 'bg-indigo-50 border-indigo-300 text-indigo-700 font-medium'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400">
          {totaal > rows.length && `${rows.length} van ${totaal} getoond · `}
          Bron: gesprekscoach (elk voice-agent-gesprek wordt beoordeeld)
        </p>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg p-3 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-500">Laden…</p>
      ) : rows.length === 0 && klassen.length === 0 ? (
        <p className="text-sm text-slate-500">
          {status === 'voorgesteld'
            ? 'Geen open leerpunten — de wachtrij is leeg.'
            : `Geen leerpunten met status "${status}".`}
        </p>
      ) : (
        <>
        {status === 'voorgesteld' && klassen.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">
              Klassen ({klassen.length}) — één besluit dekt alle leden
            </h3>
            <div className="space-y-3">
              {klassen.map((k) => {
                const inBewerking = bewerkKlasseId === k.id;
                const velden = BEWERKBAAR[k.fixtype] || [];
                return (
                  <div key={k.id} className="bg-white border-2 border-indigo-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${FIXTYPE_STIJL[k.fixtype] || 'bg-slate-100 text-slate-700'}`}
                        title={FIXTYPE_UITLEG[k.fixtype] || k.fixtype}
                      >
                        {k.fixtype}
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-indigo-600 text-white">
                        {k.leden.length}x
                      </span>
                      <span className="text-sm font-medium text-slate-800">{k.titel}</span>
                    </div>
                    <p className="text-sm text-slate-600">{k.beschrijving}</p>
                    {k.huisregel_kandidaat && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">
                        Mogelijk bewust beleid — huisregel-kandidaat bij afwijzen: {k.huisregel_kandidaat}
                      </p>
                    )}
                    {inBewerking ? (
                      <div className="mt-2 bg-indigo-50/50 border border-indigo-200 rounded-lg p-3 space-y-3">
                        {velden.map(({ key, label, lang }) => (
                          <div key={key}>
                            <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
                            {lang ? (
                              <textarea
                                value={concept[key] ?? ''}
                                onChange={(e) => setConcept((c) => ({ ...c, [key]: e.target.value }))}
                                rows={key === 'antwoord' || key === 'voorstel_tekst' ? 5 : 3}
                                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                              />
                            ) : (
                              <input
                                type="text"
                                value={concept[key] ?? ''}
                                onChange={(e) => setConcept((c) => ({ ...c, [key]: e.target.value }))}
                                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                              />
                            )}
                          </div>
                        ))}
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            Notitie (komt op de klasse én op elk lid — optioneel)
                          </label>
                          <textarea
                            value={notitie}
                            onChange={(e) => setNotitie(e.target.value)}
                            rows={2}
                            className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                          />
                        </div>
                      </div>
                    ) : (
                      <VoorstelBlok voorstel={(k.voorstel_definitief ?? k.voorstel) || null} />
                    )}
                    <details className="mt-2 text-xs text-slate-500">
                      <summary className="cursor-pointer">
                        {k.leden.length} leden{Array.isArray(k.voorbeeld_bewijs) && k.voorbeeld_bewijs.length > 0 ? ' en voorbeelden' : ''}
                      </summary>
                      <div className="mt-1 space-y-1">
                        {(k.voorbeeld_bewijs || []).map((b, i) => (
                          <p key={i} className="italic">“{b}”</p>
                        ))}
                        {k.leden.map((lid) => (
                          <div key={lid.id} className="flex items-start gap-2">
                            <span className="flex-1">{lid.leerpunt}</span>
                            {lid.call_id && (
                              <button
                                type="button"
                                onClick={() => openCall(lid.call_id!)}
                                className="text-indigo-600 hover:text-indigo-800 underline shrink-0"
                              >
                                gesprek
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <button
                        type="button"
                        disabled={busyId === k.id}
                        onClick={() => reviewKlasse(k, 'approve')}
                        className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {k.fixtype === 'kb'
                          ? `Goedkeuren → 1 artikel, ${k.leden.length} leden toegepast`
                          : `Akkoord voor alle ${k.leden.length}`}
                      </button>
                      {inBewerking ? (
                        <button
                          type="button"
                          disabled={busyId === k.id}
                          onClick={() => { setBewerkKlasseId(null); setConcept({}); setNotitie(''); }}
                          className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                        >
                          Bewerken annuleren
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={busyId === k.id || velden.length === 0}
                          onClick={() => startKlasseBewerken(k)}
                          className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                        >
                          Bewerken
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={busyId === k.id}
                        onClick={() => reviewKlasse(k, 'reject')}
                        className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                      >
                        Alle {k.leden.length} afwijzen
                      </button>
                      {busyId === k.id && <span className="text-xs text-slate-400">Bezig…</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            {inKlassen > 0 && (
              <p className="text-xs text-slate-400 mt-2">
                {inKlassen} leerpunt(en) op deze pagina zitten in de klassen hierboven; hieronder staat alleen wat los is.
              </p>
            )}
          </div>
        )}
        <div className="space-y-3">
          {losseRows.map((lp) => {
            const inBewerking = bewerkId === lp.id;
            const isBewerkt = !!lp.voorstel_definitief && !!lp.voorstel;
            const velden = BEWERKBAAR[lp.fixtype] || [];
            return (
            <div key={lp.id} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${FIXTYPE_STIJL[lp.fixtype] || 'bg-slate-100 text-slate-700'}`}
                  title={FIXTYPE_UITLEG[lp.fixtype] || lp.fixtype}
                >
                  {lp.fixtype}
                </span>
                {lp.bron === 'mens' && (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-800 text-white" title="zelf geschreven bij het naluisteren">
                    zelf geschreven
                  </span>
                )}
                {isBewerkt && (
                  <span className="text-xs text-slate-500" title="het coach-voorstel is aangepast vóór akkoord">
                    bewerkt
                  </span>
                )}
                {lp.zekerheid && lp.bron !== 'mens' && (
                  <span className="text-xs text-slate-500">zekerheid: {lp.zekerheid}</span>
                )}
                <span className="text-xs text-slate-400">
                  {new Date(lp.created_at).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
                {lp.beoordeling?.brand && (
                  <span className="text-xs text-slate-400">{lp.beoordeling.brand}</span>
                )}
                {lp.call_id && (
                  <button
                    type="button"
                    onClick={() => openCall(lp.call_id!)}
                    className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                  >
                    Gesprek openen
                  </button>
                )}
                {lp.toegepast_ref && (
                  <span className="text-xs text-emerald-700">→ {lp.toegepast_ref}</span>
                )}
              </div>

              <p className="text-sm text-slate-800">{lp.leerpunt}</p>
              {lp.bewijs && (
                <p className="text-xs text-slate-500 mt-1 whitespace-pre-wrap">Bewijs: {lp.bewijs}</p>
              )}

              {inBewerking ? (
                <div className="mt-2 bg-indigo-50/50 border border-indigo-200 rounded-lg p-3 space-y-3">
                  {velden.map(({ key, label, lang }) => (
                    <div key={key}>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
                      {lang ? (
                        <textarea
                          value={concept[key] ?? ''}
                          onChange={(e) => setConcept((c) => ({ ...c, [key]: e.target.value }))}
                          rows={key === 'antwoord' || key === 'voorstel_tekst' ? 5 : 3}
                          className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                        />
                      ) : (
                        <input
                          type="text"
                          value={concept[key] ?? ''}
                          onChange={(e) => setConcept((c) => ({ ...c, [key]: e.target.value }))}
                          className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                        />
                      )}
                    </div>
                  ))}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Notitie (waarom akkoord of afgewezen — optioneel)
                    </label>
                    <textarea
                      value={notitie}
                      onChange={(e) => setNotitie(e.target.value)}
                      rows={2}
                      className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    />
                  </div>
                  {lp.voorstel && (
                    <details className="text-xs text-slate-500">
                      <summary className="cursor-pointer">Origineel voorstel van de coach</summary>
                      <VoorstelBlok voorstel={lp.voorstel} />
                    </details>
                  )}
                </div>
              ) : (
                <>
                  <VoorstelBlok voorstel={definitief(lp)} />
                  {lp.reviewer_notitie && (
                    <p className="text-xs text-slate-500 mt-2 whitespace-pre-wrap">
                      Notitie: {lp.reviewer_notitie}
                      {lp.reviewed_by ? ` (${lp.reviewed_by})` : ''}
                    </p>
                  )}
                </>
              )}

              {lp.status !== 'toegepast' && (
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <button
                    type="button"
                    disabled={busyId === lp.id}
                    onClick={() => review(lp, 'approve')}
                    className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {lp.fixtype === 'kb' ? 'Goedkeuren → direct actief artikel' : 'Akkoord'}
                  </button>
                  {inBewerking ? (
                    <button
                      type="button"
                      disabled={busyId === lp.id}
                      onClick={stopBewerken}
                      className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Bewerken annuleren
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={busyId === lp.id || velden.length === 0}
                      onClick={() => startBewerken(lp)}
                      className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Bewerken
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={busyId === lp.id}
                    onClick={() => review(lp, 'reject')}
                    className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Afwijzen
                  </button>
                  {busyId === lp.id && <span className="text-xs text-slate-400">Bezig…</span>}
                  {lp.status !== 'voorgesteld' && !inBewerking && (
                    <span className="text-xs text-slate-400">
                      staat op {lp.status} — opnieuw beslissen mag zolang het niet is toegepast
                    </span>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
        </>
      )}
    </div>
  );
}
